import { RefObject } from "react";
import { useShallow } from "zustand/react/shallow";

import TableCellEmpty from "ds/components/Table/Row/CellEmpty";
import { selectVisibleColumns } from "ds/components/Table/store/useTableStore/selectors";
import TableSimpleCell from "ds/components/TableSimple/Cell";
import { TableSimpleStore } from "ds/components/TableSimple/types";

import SpaceDetailsAccessGrantedViaCell from "../components/AccessCells/GrantedViaCell";
import SpaceDetailsAccessGrantedViaSummaryCell from "../components/AccessCells/GrantedViaSummaryCell";
import SpaceDetailsAccessNameCell from "../components/AccessCells/NameCell";
import SpaceDetailsAccessRoleCell from "../components/AccessCells/RoleCell";
import SpaceDetailsAccessRoleSummaryCell from "../components/AccessCells/RoleSummaryCell";
import { SpaceDetailsAccessRoleBinding } from "../components/AccessCells/types";
import SpaceDetailsExpandableStickyRows, {
  ExpandableStickyRowsContext,
} from "../components/ExpandableStickyRows";
import { SPACE_IDP_GROUPS_COLUMN_ID } from "./constants";
import { SpaceDetailsIdpGroup } from "./types";

type SpaceDetailsIdpGroupsRowsProps = {
  data: SpaceDetailsIdpGroup[];
  store: TableSimpleStore;
  tableRef: RefObject<HTMLDivElement | null>;
};

const getIdpGroupId = (idpGroup: SpaceDetailsIdpGroup) => idpGroup.id;
const getIdpGroupRoleBindings = (idpGroup: SpaceDetailsIdpGroup) => idpGroup.roleBindings;
const getRoleBindingId = (binding: SpaceDetailsAccessRoleBinding) => binding.id;

const SpaceDetailsIdpGroupsRows = ({ data, store, tableRef }: SpaceDetailsIdpGroupsRowsProps) => {
  const columns = store(useShallow(selectVisibleColumns));

  const renderParentCells = (
    idpGroup: SpaceDetailsIdpGroup,
    { isExpandable, isExpanded, onToggle }: ExpandableStickyRowsContext
  ) => {
    const singleBinding = idpGroup.roleBindings[0];

    return columns.map((column, i) => {
      const cellId = `${idpGroup.id}-${column.id}`;
      const cellLayoutProps = { id: cellId, isFirst: i === 0 };

      switch (column.id) {
        case SPACE_IDP_GROUPS_COLUMN_ID.NAME:
          return (
            <SpaceDetailsAccessNameCell
              key={cellId}
              id={cellId}
              name={idpGroup.name}
              href={`/settings/idp-group/${idpGroup.id}`}
              isExpandable={isExpandable}
              isExpanded={isExpanded}
              onToggle={onToggle}
            />
          );
        case SPACE_IDP_GROUPS_COLUMN_ID.ROLE:
          if (isExpandable) {
            return (
              <SpaceDetailsAccessRoleSummaryCell
                key={cellId}
                {...cellLayoutProps}
                item={idpGroup}
              />
            );
          }
          if (singleBinding) {
            return (
              <SpaceDetailsAccessRoleCell key={cellId} {...cellLayoutProps} item={singleBinding} />
            );
          }
          return <TableSimpleCell key={cellId} {...cellLayoutProps} />;
        case SPACE_IDP_GROUPS_COLUMN_ID.GRANTED_VIA:
          if (isExpandable) {
            return (
              <SpaceDetailsAccessGrantedViaSummaryCell
                key={cellId}
                {...cellLayoutProps}
                item={idpGroup}
              />
            );
          }
          if (singleBinding) {
            return (
              <SpaceDetailsAccessGrantedViaCell
                key={cellId}
                {...cellLayoutProps}
                item={singleBinding}
              />
            );
          }
          return <TableSimpleCell key={cellId} {...cellLayoutProps} />;
        default:
          return null;
      }
    });
  };

  const renderChildCells = (
    binding: SpaceDetailsAccessRoleBinding,
    idpGroup: SpaceDetailsIdpGroup
  ) =>
    columns.map((column, i) => {
      const cellId = `${idpGroup.id}-${binding.id}-${column.id}`;
      const cellLayoutProps = { id: cellId, isFirst: i === 0 };

      switch (column.id) {
        case SPACE_IDP_GROUPS_COLUMN_ID.ROLE:
          return <SpaceDetailsAccessRoleCell key={cellId} {...cellLayoutProps} item={binding} />;
        case SPACE_IDP_GROUPS_COLUMN_ID.GRANTED_VIA:
          return (
            <SpaceDetailsAccessGrantedViaCell key={cellId} {...cellLayoutProps} item={binding} />
          );
        default:
          // Columns without a per-binding value (the name column) show a dash; the connector
          // line is drawn on the child row's first cell via CSS, whichever column that is.
          return (
            <TableSimpleCell key={cellId} id={cellId} role={i === 0 ? "rowheader" : undefined}>
              <TableCellEmpty />
            </TableSimpleCell>
          );
      }
    });

  return (
    <SpaceDetailsExpandableStickyRows
      data={data}
      tableRef={tableRef}
      getParentId={getIdpGroupId}
      getChildren={getIdpGroupRoleBindings}
      getChildId={getRoleBindingId}
      renderParentCells={renderParentCells}
      renderChildCells={renderChildCells}
    />
  );
};

export default SpaceDetailsIdpGroupsRows;
